import { log } from '@ms-ows/logging'

import { ICustomizations } from './fabric-dependencies'
import { MultiSurfaceParams, RawSurfaceParams, SurfaceParams } from './models'

// members whose values can be extended from uber-level surface params
const inheritableMultiSurfaceParamKeys: (keyof SurfaceParams)[] = [
  'anchorHint',
  'closeButtonAction',
  'closeButtonActionParams',
  'closeButtonAriaLabel',
  'backgroundColor',
  'disableHostTheme',
  'imageFetchCorsMode',
  'isControl',
  'position',
  'preventAutoDismiss',
  'smallHeadline',
]

type BaseSurfaceParamKey =
  | keyof SurfaceParams
  | keyof Pick<MultiSurfaceParams, 'itemCount'>
  | keyof RawSurfaceParams

type SurfaceParamKey = Exclude<
  Exclude<BaseSurfaceParamKey, 'onClosed'>,
  'onUpdated'
>

type ParamContext<T> = Readonly<{ alias?: SurfaceParamKey; defaultValue: T }>

const createParamContext = <T>(
  defaultValue: T,
  alias?: SurfaceParamKey
): ParamContext<T> => {
  return {
    alias,
    defaultValue,
  }
}

const numberTypeContext: ParamContext<number> = createParamContext(0)
const booleanTypeContext: ParamContext<boolean> = createParamContext(false)
const jsonTypeContext: ParamContext<object> = createParamContext({})
const stringTypeContext: ParamContext<string> = createParamContext('')

type SurfaceContext = {
  [k in SurfaceParamKey]: ParamContext<
    (SurfaceParams & MultiSurfaceParams & RawSurfaceParams)[k]
  >
}

const allSurfaceParams: SurfaceContext = {
  // SurfaceColors
  backgroundColor: stringTypeContext,
  beaconColor: stringTypeContext,
  buttonHoverColor: stringTypeContext,
  primaryButtonHoverColor: stringTypeContext,
  primaryColor: stringTypeContext,
  secondaryColor: stringTypeContext,
  textColor: stringTypeContext,

  // SurfaceMetadata
  creativeId: stringTypeContext,
  payloadId: stringTypeContext,
  surfaceName: stringTypeContext,

  // feedback urls
  actionBeaconUrl: stringTypeContext,
  actionUrl: stringTypeContext,
  impressionBeaconUrl: stringTypeContext,

  // flight related
  isAA: booleanTypeContext,
  isControl: booleanTypeContext,
  // control flag for sequence TUI
  isControl1: booleanTypeContext,
  telemetryEventName: stringTypeContext,
  treatmentType: numberTypeContext,

  // SurfaceMetadata
  delayLoad: numberTypeContext,
  eventDispatchDelay: numberTypeContext,
  fullRenderCheck: booleanTypeContext,
  preFetchDelay: numberTypeContext,
  renderCheckDelay: numberTypeContext,
  timeout: numberTypeContext,
  version: stringTypeContext,

  // ux related
  alternativeTriggerSelectors: createParamContext([]),
  anchorElementSelectors: createParamContext([]),
  anchorValidation: stringTypeContext,
  customTheme: createParamContext({} as ICustomizations),
  disableHostTheme: booleanTypeContext,

  closeButtonAction: stringTypeContext,
  closeButtonActionParams: createParamContext({}),
  primaryButtonAction: stringTypeContext,
  primaryButtonActionParams: createParamContext({}),
  secondaryButtonAction: stringTypeContext,
  secondaryButtonActionParams: createParamContext({}),

  surfaceType: createParamContext('teaching'),
  targetAction: stringTypeContext,
  targetHidingClickSelectors: createParamContext([]),
  targetHidingElementSelectors: createParamContext([]),
  useNativeRender: booleanTypeContext,

  // additional properties
  customProperties: createParamContext({} as SurfaceParams),

  // SurfaceParamsProps
  alertAriaText: stringTypeContext,
  anchorHint: createParamContext('bottomCenter'),
  closeButtonAriaLabel: stringTypeContext,
  description: stringTypeContext,
  footer: stringTypeContext,
  hasCloseButton: booleanTypeContext,
  illustrationImage: stringTypeContext,
  imageAriaLabel: stringTypeContext,
  imageFetchCorsMode: createParamContext('no-cors'),
  position: createParamContext('top'),
  preventAutoDismiss: booleanTypeContext,
  primaryButton: stringTypeContext,
  primaryButtonAriaDescription: stringTypeContext,
  primaryButtonText: createParamContext('', 'primaryButton'),
  secondaryButton: stringTypeContext,
  secondaryButtonAriaDescription: stringTypeContext,
  secondaryButtonText: createParamContext('', 'secondaryButton'),
  smallHeadline: booleanTypeContext,
  title: stringTypeContext,

  // MultiSurfaceParams
  itemCount: numberTypeContext,

  // RawSurfaceParams
  customStyles: jsonTypeContext,
  hasCoachMark: booleanTypeContext,
  localizedStyles: createParamContext({}),
}

let normalizedSurfaceParamMap: Record<string, SurfaceParamKey>

const getNormalizedSurfaceParamMap = () => {
  if (!normalizedSurfaceParamMap) {
    normalizedSurfaceParamMap = {}
    Object.keys(allSurfaceParams).forEach(key => {
      normalizedSurfaceParamMap[key.toLowerCase()] = key as SurfaceParamKey
    })
  }

  return normalizedSurfaceParamMap
}

type Params = (SurfaceParams | MultiSurfaceParams | RawSurfaceParams) & {
  [key: string]: unknown
}

const normalizeParam =
  (
    surfaceParams: Params,
    normalizedParamsKeyMap: ReturnType<typeof getNormalizedSurfaceParamMap>
  ) =>
  (key: string) => {
    const normalizedKey = normalizedParamsKeyMap[key.trim().toLowerCase()]
    if (!normalizedKey) {
      return
    }

    // normalize keys
    const paramValue = surfaceParams[key]
    const paramStringValue = typeof paramValue === 'string' && paramValue.trim()
    if (paramStringValue) {
      // tslint:disable-next-line: no-any
      surfaceParams[normalizedKey] = paramStringValue as any
    } else if (normalizedKey !== key) {
      // tslint:disable-next-line: no-any
      surfaceParams[normalizedKey] = paramValue as any
    }

    if (!paramStringValue) {
      return
    }

    // normalize values
    try {
      const paramContext = allSurfaceParams[normalizedKey]
      const paramType = paramContext && typeof paramContext.defaultValue
      switch (paramType) {
        case 'boolean':
          const lowerCaseValue = paramStringValue.toLowerCase()
          surfaceParams[normalizedKey] =
            // tslint:disable-next-line: no-any
            (lowerCaseValue[0] === 't' || lowerCaseValue[0] === 'y') as any
          break

        case 'number':
          // tslint:disable-next-line: no-any
          surfaceParams[normalizedKey] = parseInt(paramStringValue, 10) as any
          break

        default:
          if (paramStringValue[0] === '[' || paramStringValue[0] === '{') {
            // tslint:disable-next-line: no-unsafe-any
            surfaceParams[normalizedKey] = JSON.parse(paramStringValue)
          }
      }
    } catch (error) {
      log.error(
        0x2360288a /* tag_9yc8k */,
        'error parsing surface param',
        key,
        paramValue,
        error
      )
    }
  }

// fix inconsistent keys and values
export const normalizeSurfaceParams = (
  surfaceParams: Params,
  addSurfaceType = true
) => {
  const _normalizeParam = normalizeParam(
    surfaceParams,
    getNormalizedSurfaceParamMap()
  )

  Object.keys(surfaceParams).forEach(_normalizeParam)

  // inject default/fallback values
  if (addSurfaceType && !surfaceParams.surfaceType) {
    surfaceParams.surfaceType = surfaceParams.hasCoachMark
      ? 'coachmark'
      : 'teaching'
  }

  // copy generic props from surfaceParams
  const normalizeMemberProps = (memberProps: typeof surfaceParams) => {
    if (memberProps) {
      normalizeSurfaceParams(memberProps, false)

      Object.keys(memberProps).forEach(key => {
        surfaceParams[key] = memberProps[key]
      })
    }
  }

  normalizeMemberProps(surfaceParams.customStyles as typeof surfaceParams)
  normalizeMemberProps(surfaceParams.customProperties as typeof surfaceParams)

  // add values from aliases
  Object.keys(allSurfaceParams).forEach(key => {
    const surfaceParamKey = key as SurfaceParamKey
    const paramContext = allSurfaceParams[surfaceParamKey]
    const paramContextAlias = paramContext && paramContext.alias
    if (
      paramContextAlias &&
      surfaceParams[surfaceParamKey] === undefined &&
      surfaceParams[paramContextAlias] !== undefined
    ) {
      // tslint:disable-next-line: no-any
      surfaceParams[surfaceParamKey] = surfaceParams[paramContextAlias] as any
    }
  })
}

// extract SurfaceParams object from MultiSurfaceParams
export const createSurfaceParamsByIndex = (
  renderIndex: number,
  surfaceParams: Readonly<MultiSurfaceParams>
): SurfaceParams => {
  const renderIndexSuffix = renderIndex.toString()
  const surfaceObj: { [key: string]: unknown } = {}
  for (const key of Object.keys(surfaceParams)) {
    // TODO: only 1-10 indexes are supported
    if (key.endsWith(renderIndexSuffix)) {
      const keyWithOutSuffix = key.substring(0, key.length - 1)
      surfaceObj[keyWithOutSuffix] = surfaceParams[key]
    }
  }

  normalizeSurfaceParams(surfaceObj as SurfaceParams)

  // fallback to 'teaching' if surfaceType at the index is not specified
  surfaceObj.surfaceType = surfaceObj.surfaceType || 'teaching'

  // use top-level surface prop if the individual surface does not have the value set
  inheritableMultiSurfaceParamKeys.forEach(key => {
    if (surfaceObj[key] === undefined && surfaceParams[key] !== undefined) {
      surfaceObj[key] = surfaceParams[key]
    }
  })

  // add default footer for sequenced UI surfaces
  if (
    surfaceParams.itemCount > 1 &&
    surfaceObj.surfaceType === 'teaching' &&
    surfaceObj.footer === undefined
  ) {
    surfaceObj.footer = `${renderIndex} of ${surfaceParams.itemCount}`
  }

  return surfaceObj as SurfaceParams
}
